import {useEffect, useState} from 'react';
import {PageTitle} from '../../../_metronic/layout/core';
import {useIntl} from 'react-intl';
import {createSearchParams, useNavigate, useSearchParams} from 'react-router-dom';
import TabComponent from '../../../app/common/components/TabComponent';
import ConfigurationEndMonitoring from '../Components/Telephonie/ConfigurationEndMonitoring';
import Outils from '../Components/Telephonie/Outils';
import Gestion4G from '../Components/Telephonie/Gestion4G';
import {useGetMe} from '../../../app/hooks/useGetMe';
import {isClientCIS} from '../../Auth/Utils/CustomerHelper';
import RedirectionModal from '../../../app/common/components/modal/RedirectionModal';
import {useRedirectionModal} from '../../../app/common/components/modal/hooks/useRedirectionModal';
import {Card} from 'react-bootstrap';
import Level3Title from '../../../app/common/components/title/Level3Title';
import {getTelephonyDetailsPathname} from '../Helpers/ServiceHelper';

const RedirectTabContent = ({
  label,
  description,
  url,
  onRedirect,
}: {
  label: string;
  description: string;
  url: string;
  onRedirect: (url: string) => void;
}): JSX.Element => {
  const intl = useIntl();

  return (
    <div className="row g-4 mt-1">
      <div className="col-12 col-xxl-6">
        <Card>
          <Card.Header>
            <Card.Title>
              <Level3Title title={label} />
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <p>{description}</p>
            <button className="btn btn-primary" onClick={() => onRedirect(url)}>
              {intl.formatMessage(
                {id: 'CONFIGURATION.TEL.ACCESS_BUTTON'},
                {label: label.toLowerCase()}
              )}
            </button>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

const TelephonyConfigPage = (): JSX.Element => {
  const intl = useIntl();
  const customer = useGetMe();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cisId: string = searchParams.get('cisId') ?? '';
  const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (customer) {
      setActiveTab(
        (prev) => prev ?? (isClientCIS(customer) ? 'confandmonitoring' : 'desk-services')
      );
    }
  }, [customer]);

  const {isOpen, closeModal, confirmRedirection, handleContinue, pendingUrl, countdown} =
    useRedirectionModal();

  const deskTab = (labelId: string, targetUrl: string, key: string, description: string) => ({
    title: intl.formatMessage({id: labelId}),
    key,
    content: (
      <RedirectTabContent
        label={intl.formatMessage({id: labelId})}
        description={description}
        url={`/ec-desk?next=${encodeURIComponent(targetUrl)}`}
        onRedirect={confirmRedirection}
      />
    ),
  });

  const cisExtraTabs = [
    {
      title: intl.formatMessage({id: 'CONFIGURATION.TEL.CONF_MYTELEPHONY'}),
      key: 'confandmonitoring',
      content: <ConfigurationEndMonitoring CISID={cisId} />,
    },
    {
      title: intl.formatMessage({id: 'CONFIGURATION.TEL.GESTION_MYISTRA'}),
      key: 'outils',
      content: <Outils />,
    },
    {
      title: intl.formatMessage({id: 'CONFIGURATION.TEL.GESTION'}),
      key: 'tel-gestion',
      content: (
        <Gestion4G
          onAccess={() =>
            cisId
              ? navigate({
                  pathname: getTelephonyDetailsPathname(cisId),
                  search: createSearchParams({tab: 'parc4g'}).toString(),
                })
              : navigate('/services/telephonie/liste')
          }
        />
      ),
    },
    // ecv1Tab('CONFIGURATION.TEL.CONF', 'telephonie/telephony', 'tel-conf'),
    // ecv1Tab('CONFIGURATION.TEL.OUTILS', 'telephonie/tools', 'tel-outils'),
  ];

  const deskExtraTabs = [
    deskTab(
      'CONFIGURATION.SERVICES',
      'https://mobile.stella-telecom.fr/services',
      'desk-services',
      intl.formatMessage({id: 'CONFIGURATION.SERVICES.DESCRIPTION'})
    ),
    deskTab(
      'CONFIGURATION.TELFIXE',
      'https://extranet2.stella-telecom.fr/telephonie-fixe',
      'desk-telfixe',
      intl.formatMessage({id: 'CONFIGURATION.TELFIXE.DESCRIPTION'})
    ),
    deskTab(
      'CONFIGURATION.SERVICESTELFIXE',
      'https://telfixe.stella-telecom.fr/services',
      'desk-servicestelfixe',
      intl.formatMessage({id: 'CONFIGURATION.SERVICESTELFIXE.DESCRIPTION'})
    ),
    deskTab(
      'DOCUMENTS.TELEPHONIE.FAX',
      'https://extranet2.stella-telecom.fr/fax',
      'desk-fax',
      intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE.FAX.DESCRIPTION'})
    ),
    deskTab(
      'DOCUMENTS.TELEPHONIE.ANNUAIRE',
      'https://annuaire.stella-telecom.fr/',
      'desk-annuaire',
      intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE.ANNUAIRE.DESCRIPTION'})
    ),
    deskTab(
      'DOCUMENTS.TELEPHONIE.MOBILE',
      'https://extranet2.stella-telecom.fr/telephonie-mobile/redirection',
      'desk-mobile',
      intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE.MOBILE.DESCRIPTION'})
    ),
    deskTab(
      'DOCUMENTS.TELEPHONIE.SMS',
      'https://extranet2.stella-telecom.fr/sms',
      'desk-sms',
      intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE.SMS.DESCRIPTION'})
    ),
  ];

  const extraTabs = customer ? (isClientCIS(customer) ? cisExtraTabs : deskExtraTabs) : [];

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.TELEPHONY'})}</PageTitle>
      {extraTabs.length > 0 && (
      <TabComponent
        tabData={extraTabs}
        activeKey={activeTab}
        onTabChange={setActiveTab}
      />
      )}
      <RedirectionModal
        show={isOpen}
        onCancel={closeModal}
        onContinue={handleContinue}
        url={pendingUrl || ''}
        countdown={countdown}
      />
    </>
  );
};

export default TelephonyConfigPage;
